import { Extension, type Editor } from '@tiptap/react';
import Image from '@tiptap/extension-image';

/** 들여쓰기 한 단계 폭(em)과 최대 단계 */
const INDENT_STEP_EM = 2;
const MAX_INDENT = 8;

/** 들여쓰기·줄 간격을 거는 블록. 목록 항목은 단계(sink/lift)로 처리한다. */
const BLOCK_TYPES = ['paragraph', 'heading'];
const LIST_ITEM_TYPES = ['listItem', 'taskItem'] as const;

type PmMarks = NonNullable<Editor['state']['storedMarks']>;

export type ImageAlign = 'left' | 'center' | 'right';

/**
 * 문단 단위 서식(들여쓰기·줄 간격).
 * 줄 간격은 글자 span 이 아닌 문단 속성이어야 줄 사이 간격이 고르게 잡힌다.
 */
export const ParagraphFormat = Extension.create({
  name: 'paragraphFormat',

  addGlobalAttributes() {
    return [
      {
        types: BLOCK_TYPES,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const level = Number(el.getAttribute('data-indent'));
              return Number.isFinite(level) ? clampIndent(level) : 0;
            },
            renderHTML: (attrs) =>
              attrs.indent
                ? {
                    'data-indent': attrs.indent,
                    style: `margin-left: ${attrs.indent * INDENT_STEP_EM}em`,
                  }
                : {},
          },
          lineHeight: {
            default: null,
            parseHTML: (el) => el.style.lineHeight || null,
            renderHTML: (attrs) =>
              attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {},
          },
        },
      },
    ];
  },

  addKeyboardShortcuts() {
    // Outlook 단축키: Ctrl+M 들여쓰기, Ctrl+Shift+M 내어쓰기
    return {
      'Mod-m': () => changeIndent(this.editor, 1),
      'Mod-Shift-m': () => changeIndent(this.editor, -1),
    };
  },
});

function clampIndent(level: number): number {
  return Math.min(Math.max(Math.trunc(level), 0), MAX_INDENT);
}

/** 선택 영역 문단 들여쓰기(+1)/내어쓰기(-1). 목록 안이면 항목 단계를 옮긴다. */
export function changeIndent(editor: Editor, dir: 1 | -1): boolean {
  const listItem = LIST_ITEM_TYPES.find((type) => editor.isActive(type));
  if (listItem) {
    return dir > 0
      ? editor.chain().focus().sinkListItem(listItem).run()
      : editor.chain().focus().liftListItem(listItem).run();
  }
  return editor
    .chain()
    .focus()
    .command(({ tr }) => {
      let changed = false;
      tr.doc.nodesBetween(tr.selection.from, tr.selection.to, (node, pos) => {
        if (!BLOCK_TYPES.includes(node.type.name)) return;
        const next = clampIndent((node.attrs.indent ?? 0) + dir);
        if (next === node.attrs.indent) return;
        tr.setNodeAttribute(pos, 'indent', next);
        changed = true;
      });
      return changed;
    })
    .run();
}

/** 선택 영역 문단 줄 간격. null 이면 기본값으로 되돌린다. */
export function setBlockLineHeight(editor: Editor, lineHeight: string | null): boolean {
  return editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.doc.nodesBetween(tr.selection.from, tr.selection.to, (node, pos) => {
        if (BLOCK_TYPES.includes(node.type.name)) {
          tr.setNodeAttribute(pos, 'lineHeight', lineHeight);
        }
      });
      return true;
    })
    .run();
}

/** 서식 지우기 — 글자 서식·정렬·들여쓰기·줄 간격을 걷어낸다. 블록 종류(제목·목록)는 남긴다. */
export function clearFormatting(editor: Editor): boolean {
  return editor
    .chain()
    .focus()
    .unsetAllMarks()
    .unsetTextAlign()
    .command(({ tr }) => {
      tr.doc.nodesBetween(tr.selection.from, tr.selection.to, (node, pos) => {
        if (!BLOCK_TYPES.includes(node.type.name)) return;
        if (node.attrs.indent) tr.setNodeAttribute(pos, 'indent', 0);
        if (node.attrs.lineHeight) tr.setNodeAttribute(pos, 'lineHeight', null);
      });
      return true;
    })
    .run();
}

/** 서식 복사 — 선택 첫 글자(또는 커서 위치)의 글자 서식. 링크는 복사하지 않는다. */
export function captureMarks(editor: Editor): PmMarks {
  const { state } = editor;
  const { from, empty, $from } = state.selection;
  const node = empty ? null : state.doc.nodeAt(from);
  const marks = node?.isText ? node.marks : (state.storedMarks ?? $from.marks());
  return marks.filter((mark) => mark.type.name !== 'link');
}

/** 서식 붙여넣기 — 선택 영역의 글자 서식을 복사한 서식으로 바꾼다(링크는 유지). */
export function applyMarks(editor: Editor, marks: PmMarks): boolean {
  return editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      const { from, to, empty } = tr.selection;
      if (empty) return false;
      for (const type of Object.values(state.schema.marks)) {
        if (type.name !== 'link') tr.removeMark(from, to, type);
      }
      for (const mark of marks) tr.addMark(from, to, mark);
      return true;
    })
    .run();
}

/** 링크 주소 보정 — 스킴이 없으면 https(이메일이면 mailto)를 붙인다. javascript: 등은 https 아래로 무력화된다. */
export function normalizeLinkUrl(raw: string): string {
  const url = raw.trim();
  if (/^(https?:|mailto:|tel:)/i.test(url) || url.startsWith('/') || url.startsWith('#')) return url;
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(url)) return `mailto:${url}`;
  return `https://${url}`;
}

/**
 * 본문 이미지 — 정렬(align) 속성 추가.
 * 리사이즈 노드뷰는 속성 변경 시 DOM 을 다시 그리지 않으므로, 정렬 변경·원래 크기 복원 때는 노드뷰를 새로 만든다.
 */
export const BoardImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-align'),
        renderHTML: (attrs) => (attrs.align ? { 'data-align': attrs.align } : {}),
      },
    };
  },

  addNodeView() {
    const factory = this.parent?.();
    if (!factory) return null;
    return (props) => {
      const view = factory(props);
      const align = props.node.attrs.align as ImageAlign | null;
      if (view.dom instanceof HTMLElement) view.dom.dataset.align = align ?? 'left';
      const update = view.update?.bind(view);
      let current = props.node;
      view.update = (node, decorations, innerDecorations) => {
        const prev = current;
        current = node;
        if (node.attrs.align !== prev.attrs.align) return false;
        if (node.attrs.width == null && prev.attrs.width != null) return false;
        return update ? update(node, decorations, innerDecorations) : true;
      };
      return view;
    };
  },
});
